import { defineStore } from 'pinia'
import { CategoryModel } from '@/models/CategoryModel'
import { ProductModel } from '@/models/ProductModel'
import { BrandModel } from '@/models/BrandModel'
import api from '@/api'

// The spec filters as query params: s12=205|215 (values may hold commas).
export const specParams = (specs) =>
  Object.fromEntries(Object.entries(specs || {}).filter(([, v]) => v?.length).map(([id, v]) => ['s' + id, v.join('|')]))

export const useShopStore = defineStore('shop', {
  state: () => ({
    categories: [],
    brands: [],
    products: [],
    loadingCategories: false,
    loadingProducts: false,
    _productRequestId: 0,
    // The list state the shown products were loaded for.
    loadedKey: '',

    // filters
    categoryId: null,
    brandId: null,
    search: '',
    // Price range in euros incl. VAT, either end optional.
    priceMin: null,
    priceMax: null,
    showInactive: false,
    // Spec filter selections, spec definition id → values; the category's filters and their values.
    specs: {},
    facets: [],
    // Empty = the default below; a user-picked sort goes here.
    sort: '',

    // pagination
    page: 1,
    pageSize: 24,
    total: 0,

    // detail
    selectedProduct: null,

    // offer inquiry
    offerProducts: [],
    offerOpen: false,
  }),

  getters: {
    filteredProducts(state) {
      return state.products
    },
    offerCount(state) {
      return state.offerProducts.length
    },
    totalPages(state) {
      return Math.max(1, Math.ceil(state.total / state.pageSize))
    },
  },

  actions: {
    async load() {
      await Promise.all([this.loadCategories(), this.loadProducts()])
    },

    async loadCategories() {
      this.loadingCategories = true
      try {
        const [categories, brands] = await Promise.all([
          new CategoryModel().find({ asPlainData: true }),
          new BrandModel().find({ asPlainData: true }),
        ])
        this.categories = categories.data ?? categories
        this.brands = brands.data ?? brands
      } catch (e) {
        console.warn('Failed to load categories', e)
      } finally {
        this.loadingCategories = false
      }
    },

    listKey() {
      return JSON.stringify([this.categoryId, this.search.trim(), this.brandId, this.priceMin, this.priceMax, this.page, this.showInactive, specParams(this.specs)])
    },

    // Search, brand and price range, shared by the product list and the category's filters.
    scopeParams() {
      const params = {}
      if (this.brandId) params.brandId = this.brandId
      if (this.search.trim()) params.search = this.search.trim()
      if (this.priceMin) params.priceMin = this.priceMin
      if (this.priceMax) params.priceMax = this.priceMax
      if (this.showInactive) params.admin = true
      return params
    },

    async loadProducts() {
      const requestId = ++this._productRequestId
      this.loadedKey = this.listKey()
      this.loadingProducts = true
      try {
        const params = { include: 'category,brand,energyLabels,variants,variantGroups' }
        if (this.categoryId) params.categoryId = this.categoryId
        // Default for Kõik tooted: featured first, then the categories' own order.
        const order = this.sort || (this.categoryId ? '' : '-isFeatured,category.order,name')
        if (order) params.order = order
        Object.assign(params, this.scopeParams(), specParams(this.specs))
        this.loadFacets(requestId)
        const { data, total } = await new ProductModel().find({ asPlainData: true, params, limit: this.pageSize, page: this.page })
        if (requestId !== this._productRequestId) return
        this.products = data
        this.total = total
      } catch (e) {
        if (requestId !== this._productRequestId) return
        console.warn('Failed to load products', e)
      } finally {
        if (requestId === this._productRequestId) this.loadingProducts = false
      }
    },

    // The category's filters with the values the current selection leaves.
    async loadFacets(requestId) {
      if (!this.categoryId) {
        this.facets = []
        return
      }
      const params = { ...this.scopeParams(), ...specParams(this.specs) }
      try {
        const { data } = await api.get(`/categories/${this.categoryId}/filters`, { params })
        if (requestId === this._productRequestId) this.facets = data
      } catch (e) {
        console.warn('Failed to load filters', e)
      }
    },

    applyFilters() {
      this.page = 1
      this.loadProducts()
    },

    goToPage(page) {
      this.page = Math.min(Math.max(1, page), this.totalPages)
      this.loadProducts()
    },

    resetFilters() {
      this.categoryId = null
      this.brandId = null
      this.search = ''
      this.priceMin = null
      this.priceMax = null
      this.specs = {}
      this.applyFilters()
    },

    selectProduct(product) {
      this.selectedProduct = product
    },

    clearSelection() {
      this.selectedProduct = null
    },

    addToOffer(product) {
      // Each size or colour of a product is its own line.
      const key = `${product.id}|${product.variation || ''}`
      if (!this.offerProducts.find((p) => p.key === key)) {
        this.offerProducts.push({
          key,
          id: product.id,
          name: product.name,
          price: product.price,
          variation: product.variation || null,
        })
      }
    },

    removeFromOffer(key) {
      this.offerProducts = this.offerProducts.filter((p) => p.key !== key)
    },

    clearOffer() {
      this.offerProducts = []
    },
  },
})
